const DAY_MS = 24 * 60 * 60 * 1000;

export function parseLocalDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  const [year, month, day] = String(value).slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

export function formatLocalDate(value) {
  const date = parseLocalDate(value);
  return date ? date.toLocaleDateString('pt-BR') : 'data não informada';
}

export function ageInDays(value, now = new Date()) {
  const date = parseLocalDate(value);
  if (!date) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0, 0);
  return Math.max(0, Math.floor((today.getTime() - date.getTime()) / DAY_MS));
}

export function isIrrigationDataStale(value, now = new Date(), maximumAgeDays = 2) {
  const age = ageInDays(value, now);
  return age === null || age > maximumAgeDays;
}

export function normalizeRecommendation(value) {
  switch (value) {
    case 'urgente':
    case 'irrigar_urgente': return 'urgente';
    case 'irrigar': return 'irrigar';
    case 'aguardar':
    case 'aguardar_chuva': return 'aguardar';
    default: return 'ok';
  }
}

function getIrrigationConfig(item = {}) {
  const config = item.configuracao_irrigacao ?? item.talhao?.configuracao_irrigacao;
  return Array.isArray(config) ? config[0] : config;
}

export function calculatePivotSetting(needMm, config = {}) {
  if (config?.sistema_irrigacao !== 'pivo_central') return null;

  const targetMm = Number(needMm);
  const depthAt100Mm = Number(config.lamina_pivo_100_mm);
  const configuredMinimum = Number(config.percentimetro_minimo);
  const minimumPercent = Number.isFinite(configuredMinimum)
    ? Math.min(100, Math.max(1, configuredMinimum))
    : 10;

  if (!Number.isFinite(targetMm) || targetMm <= 0 || !Number.isFinite(depthAt100Mm) || depthAt100Mm <= 0) {
    return null;
  }

  const maximumDepthPerPass = depthAt100Mm * (100 / minimumPercent);
  const passes = Math.max(1, Math.ceil(targetMm / maximumDepthPerPass));
  const exactPercent = (passes * depthAt100Mm * 100) / targetMm;
  const percent = Math.min(100, Math.max(minimumPercent, Math.floor(exactPercent)));
  const depthPerPassMm = depthAt100Mm * (100 / percent);
  const estimatedTotalMm = depthPerPassMm * passes;

  return {
    percent,
    passes,
    depthPerPassMm,
    estimatedTotalMm,
    targetMm,
    minimumApplication: targetMm < depthAt100Mm,
  };
}

export function normalizeBalanceItem(item = {}) {
  const percent = item.armazenamentoPercentual ?? item.armazenamento_percentual;
  const need = item.necessidadeMm ?? item.necessidade_irrigacao;
  const needMm = Number.isFinite(Number(need)) ? Number(need) : 0;
  return {
    raw: item,
    id: item.id,
    status: normalizeRecommendation(item.recomendacao),
    nome: item.nome || item.talhao?.nome || 'Talhão',
    cultura: item.cultura || item.talhao?.cultura || 'Cultura não informada',
    area: item.area ?? item.talhao?.area,
    armazenamentoPercentual: Number.isFinite(Number(percent)) ? Number(percent) : null,
    necessidadeMm: needMm,
    pivotSetting: calculatePivotSetting(needMm, getIrrigationConfig(item)),
    tempoAplicacao: item.tempoAplicacao || null,
    janela: item.janela || null,
    fonte: item.fonte || item.metodo_calculo_et0 || null,
  };
}

export function deriveIrrigationSummary(items = [], base = {}) {
  const normalized = items.map(normalizeBalanceItem);
  const count = status => normalized.filter(item => item.status === status).length;
  return {
    ...base,
    total_talhoes: normalized.length,
    talhoes_manejo_ativo: normalized.length,
    talhoes_urgente: count('urgente'),
    talhoes_irrigar: count('irrigar'),
    talhoes_aguardar: count('aguardar'),
    talhoes_ok: count('ok'),
  };
}

export function getPriorityBalance(items = []) {
  const normalized = items.map(normalizeBalanceItem);
  return normalized.find(item => item.status === 'urgente')
    || normalized.find(item => item.status === 'irrigar')
    || normalized[0]
    || null;
}
